/**
 * TaskViewerModal Component
 *
 * Read-only modal that displays complete task details including:
 * - Task information (name, description, project, status, phase, dates)
 * - Assigned vendor
 * - Task attachments from "Attachments" field and linked "Files & Photos"
 * - Viewer for images and PDFs within the modal
 * - Internal and task notes
 * - Additional flags (required for next phase, include in digest)
 * - Edit button to open TaskModal for editing
 *
 * @component
 */

import React, { useState } from "react";
import {
  FaTimes,
  FaEdit,
  FaCalendar,
  FaHardHat,
  FaClock,
  FaPaperclip,
  FaHammer,
  FaNewspaper,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList,
} from "react-icons/fa";
import { formatDateET } from "../../utils/dateUtils";
import "./TaskViewerModal.css";

/**
 * TaskViewerModal Component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.task - Task object with all fields
 * @param {Function} props.onEdit - Edit button handler
 */
const TaskViewerModal = ({ isOpen, onClose, task, onEdit }) => {
  // State for attachment viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [allAttachments, setAllAttachments] = useState([]);

  if (!isOpen || !task) return null;

  // Helper to get status class (matches exact status values)
  const getStatusClass = () => {
    switch (task.status) {
      case "Completed":
        return "tvm-badge-completed";
      case "In progress":
        return "tvm-badge-in-progress";
      case "Delayed":
        return "tvm-badge-delayed";
      case "Not started":
      default:
        return "tvm-badge-not-started";
    }
  };

  // Format date range
  const getDateRange = () => {
    if (task.status === "Completed" && task.dateCompleted) {
      return `${formatDateET(task.startDate)} → ${formatDateET(
        task.dateCompleted,
      )}`;
    }
    if (task.startDate && task.endDate) {
      return `${formatDateET(task.startDate)} → ${formatDateET(task.endDate)}`;
    }
    if (task.startDate) {
      return formatDateET(task.startDate);
    }
    return "No date";
  };

  // Combine all attachments (task attachments + files & photos)
  const getCombinedAttachments = () => {
    const combined = [];

    // Add direct task attachments
    if (task.attachments && task.attachments.length > 0) {
      task.attachments.forEach((att) => {
        combined.push({
          ...att,
          source: "Task Attachment",
        });
      });
    }

    // Add linked files & photos
    if (task.filesAndPhotos && task.filesAndPhotos.length > 0) {
      task.filesAndPhotos.forEach((file) => {
        combined.push({
          ...file,
          source: "Linked File",
        });
      });
    }
    return combined;
  };

  // Open attachment viewer
  const openViewer = (attachment, index, attachments) => {
    setCurrentAttachment(attachment);
    setCurrentIndex(index);
    setAllAttachments(attachments);
    setViewerOpen(true);
  };

  // Close attachment viewer
  const closeViewer = () => {
    setViewerOpen(false);
    setCurrentAttachment(null);
  };

  // Navigate to previous attachment
  const prevAttachment = () => {
    const newIndex =
      currentIndex > 0 ? currentIndex - 1 : allAttachments.length - 1;
    setCurrentIndex(newIndex);
    setCurrentAttachment(allAttachments[newIndex]);
  };

  // Navigate to next attachment
  const nextAttachment = () => {
    const newIndex =
      currentIndex < allAttachments.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setCurrentAttachment(allAttachments[newIndex]);
  };

  // Check if attachment is image
  const isImage = (file) => {
    return file.type?.startsWith("image/");
  };

  // Check if attachment is PDF
  const isPDF = (file) => {
    return (
      file.type === "application/pdf" ||
      file.filename?.toLowerCase().endsWith(".pdf")
    );
  };

  const combinedAttachments = getCombinedAttachments();

  return (
    <>
      <div className="tvm-overlay" onClick={onClose}>
        <div className="tvm-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="tvm-header">
            <div className="tvm-title-row">
              <div className="tvm-header-icon">
                <FaClipboardList />
              </div>
              <div className="tvm-title-text">
                <span>{task.projectName}</span>
                <h3>{task.taskName}</h3>
              </div>
            </div>
            <div className="tvm-header-actions">
              <button className="tvm-edit-btn" onClick={onEdit}>
                <FaEdit /> Edit
              </button>
              <button
                className="tvm-close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>{" "}
          </div>
          {/* Content */}
          <div className="tvm-content">
            {/* Status & Phase Pills */}
            <div className="tvm-badges">
              {task.status && (
                <span
                  className={`tvm-badge tvm-badge-status ${getStatusClass()}`}
                >
                  {task.status}
                </span>
              )}
              {task.constructionPhase && (
                <span className="tvm-badge tvm-badge-phase">
                  {task.constructionPhase}
                </span>
              )}
            </div>
            <div className="tvm-task-info">
              {/* Date Info */}
              <div className="tvm-info-box">
                <div className="tvm-info-field">
                  <span className="tvm-label">
                    <FaCalendar /> Date Range
                  </span>
                  <span className="tvm-value">{getDateRange()}</span>
                </div>
                {task.duration && (
                  <div className="tvm-info-field">
                    <span className="tvm-label">
                      <FaClock /> Duration
                    </span>
                    <span className="tvm-value">{task.duration} days</span>
                  </div>
                )}
                {task.workType && (
                  <div className="tvm-info-field">
                    <span className="tvm-label">
                      <FaHammer /> Work Type
                    </span>
                    <span className="tvm-value">{task.workType}</span>
                  </div>
                )}
                {task.vendorName && (
                  <div className="tvm-info-field">
                    <span className="tvm-label">
                      <FaHardHat /> Assigned Vendor
                    </span>
                    <span className="tvm-value">{task.vendorName}</span>
                  </div>
                )}
                <div className="tvm-info-field">
                  <span className="tvm-label">
                    <FaNewspaper /> Included in Digest?
                  </span>
                  <span className="tvm-value">
                    {task.includeInDigest ? "✅" : "❌"}
                  </span>
                </div>
              </div>
              {/* Description */}
              {task.taskDescription && (
                <div className="tvm-section">
                  <div className="tvm-field">
                    <span className="tvm-label">Description</span>
                    <p className="tvm-description">{task.taskDescription}</p>
                  </div>
                </div>
              )}
              {task.taskNotes || task.internalNotes ? (
                <div className="tvm-section-full">
                  {/* Task Notes */}
                  {task.taskNotes && (
                    <div className="tvm-field">
                      <span className="tvm-label">Task Notes (for Client)</span>
                      <p className="tvm-description">{task.taskNotes}</p>
                    </div>
                  )}
                  {/* Internal Notes */}
                  {task.internalNotes && (
                    <div className="tvm-field tvm-internal-notes">
                      <span className="tvm-label">
                        Internal Notes (Admin Only)
                      </span>
                      <p className="tvm-description">{task.internalNotes}</p>
                    </div>
                  )}
                </div>
              ) : null}

              {/* ALL ATTACHMENTS */}
              {combinedAttachments.length > 0 && (
                <div className="tvm-section-full">
                  <div className="tvm-field-full">
                    <span className="tvm-label">
                      <FaPaperclip /> Attachments & Photos (
                      {combinedAttachments.length})
                    </span>
                    <div className="tvm-attachments">
                      {combinedAttachments.map((file, index) => (
                        <div
                          key={`${file.source}-${file.id}-${index}`}
                          className="tvm-attachment-item"
                          onClick={() =>
                            openViewer(file, index, combinedAttachments)
                          }
                        >
                          <span data-tooltip-br="Click to view">
                            {isImage(file) ? (
                              <img
                                src={file.thumbnails?.large?.url || file.url}
                                alt={file.filename}
                                className="tvm-attachment-thumb"
                              />
                            ) : isPDF(file) ? (
                              <div className="tvm-attachment-file tvm-attachment-pdf">
                                <FaPaperclip />
                                <span>PDF</span>
                                <span className="tvm-filename">
                                  {file.filename}
                                </span>
                              </div>
                            ) : (
                              <div className="tvm-attachment-file">
                                <FaPaperclip />
                                <span className="tvm-filename">
                                  {file.filename}
                                </span>
                              </div>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Attachment Viewer Modal - Overlays on top of task viewer */}
      {viewerOpen && currentAttachment && (
        <div className="tvm-viewer-overlay" onClick={closeViewer}>
          <div
            className="tvm-viewer-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Viewer Header */}
            <div className="tvm-viewer-header">
              <div className="tvm-viewer-info">
                <span className="tvm-viewer-filename">
                  {currentAttachment.filename}
                </span>
                <span className="tvm-viewer-counter">
                  {currentIndex + 1} / {allAttachments.length}
                </span>
              </div>
              <button className="tvm-viewer-close" onClick={closeViewer}>
                <FaTimes />
              </button>
            </div>

            {/* Viewer Content */}
            <div className="tvm-viewer-content">
              {isImage(currentAttachment) ? (
                <img
                  src={currentAttachment.url}
                  alt={currentAttachment.filename}
                  className="tvm-viewer-image"
                />
              ) : isPDF(currentAttachment) ? (
                <iframe
                  src={currentAttachment.url}
                  title={currentAttachment.filename}
                  className="tvm-viewer-pdf"
                />
              ) : (
                <div className="tvm-viewer-unsupported">
                  <FaPaperclip size={48} />
                  <p>Preview not available for this file type</p>
                  <a
                    href={currentAttachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tvm-viewer-download"
                  >
                    Download File
                  </a>
                </div>
              )}
            </div>

            {/* Navigation Arrows */}
            {allAttachments.length > 1 && (
              <>
                <button
                  className="tvm-viewer-nav tvm-viewer-prev"
                  onClick={prevAttachment}
                >
                  <FaChevronLeft />
                </button>
                <button
                  className="tvm-viewer-nav tvm-viewer-next"
                  onClick={nextAttachment}
                >
                  <FaChevronRight />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TaskViewerModal;
